import { useEffect, useRef, ReactNode } from 'react';
import clsx from 'clsx';
import { useStyles } from './overlay-dialog.style';
import { Dialog, DialogTitle, DialogActions, DialogContent, DialogProps, Typography } from '@material-ui/core';
import { CloseButton, closeButtonProps } from '../close-button/close-button';

type overlayDialogProps = {
    /**
     * Title of the dialog. accept both string and jsx elements
     */
    title?: ReactNode | string;
    /**
     * Footer of the dialog. accept jsx elements
     */
    dialogAction?: JSX.Element;
    /**
     * Show/hide close button.
     * If both close button & title is false dialog title will not render
     */
    closeButton?: boolean;
    /**
     * Add divider on top and bottom of the content
     */
    divider?: boolean;
} & DialogProps;

// Just a convinience type that consumers may use if they like
interface IOverlayDialogState {
    show: boolean;
    content: JSX.Element;
}

const OverlayDialog = ({ title, dialogAction, closeButton = true, divider = false, ...rest }: overlayDialogProps) => {
    const classes = useStyles();
    const descriptionElementRef = useRef<HTMLElement>(null);

    useEffect(() => {
        if (rest.open && !rest.scroll) {
            const { current: descriptionElement } = descriptionElementRef;
            if (descriptionElement !== null) {
                descriptionElement.focus();
            }
        }
    }, [rest.open, rest.scroll]);

    return (
        <Dialog
            BackdropProps={{
                classes: {
                    root: classes.backDropOverlay,
                },
            }}
            PaperProps={{
                classes: {
                    root: classes.paper,
                },
            }}
            aria-labelledby={rest['aria-labelledby'] || 'overlay-dialog-title'}
            aria-describedby={rest['aria-describedby'] || 'overlay-dialog-description'}
            {...rest}
        >
            {((title && title !== '') || closeButton) && (
                <DialogTitle
                    className={clsx(title && title !== '' ? classes.dialogTitle : classes.dialogTitleEmpty)}
                    disableTypography
                    id={rest['aria-labelledby'] || 'overlay-dialog-title'}
                >
                    <Typography variant="h6">{title}</Typography>
                    {closeButton ? (
                        <span className={classes.closeButton}>
                            <CloseButton onClickEvent={rest.onClose as closeButtonProps['onClickEvent']} />
                        </span>
                    ) : null}
                </DialogTitle>
            )}
            <DialogContent
                dividers={divider}
                className={classes.dialogContent}
                id={rest['aria-describedby'] || 'overlay-dialog-description'}
                ref={descriptionElementRef}
                tabIndex={-1}
            >
                {rest.children}
            </DialogContent>
            {dialogAction && <DialogActions className={classes.dialogAction}>{dialogAction}</DialogActions>}
        </Dialog>
    );
};

export { OverlayDialog };
export type { IOverlayDialogState };
export type { overlayDialogProps };
